/**
 * LiveConnectPanel — manually introduce two graph nodes.
 *
 * Two NodeSearchBars (From / To) + an optional intro note + a Connect
 * button. POSTs /api/shura/connect which:
 *   1. records the connection (shows as a live edge),
 *   2. opens a chat thread between the two principals,
 *   3. emails both parties (CC'd to each other).
 *
 * Renders as a slim card right under the header search bar.
 */
import React, { useState } from 'react';
import { ArrowRight, Link2, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import NodeSearchBar from './NodeSearchBar';

export default function LiveConnectPanel({ nodes, isDark, onConnected }) {
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fromNode = nodes.find((n) => n.id === fromId);
  const toNode   = nodes.find((n) => n.id === toId);
  const canSubmit = fromId && toId && fromId !== toId && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post('/shura/connect', {
        from_node_id: fromId,
        to_node_id: toId,
        intro_message: note.trim() || undefined,
      });
      setLastResult(data);
      toast.success(
        data.emailed?.length
          ? `Intro sent — ${data.emailed.length} email${data.emailed.length === 1 ? '' : 's'} delivered.`
          : 'Connection recorded.',
      );
      setFromId(null); setToId(null); setNote('');
      onConnected?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connect failed.');
    } finally { setBusy(false); }
  };

  const tone = isDark
    ? 'bg-white/[0.03] border-white/10'
    : 'bg-white border-slate-200';

  return (
    <div
      data-testid="shura-live-connect"
      className={`rounded-lg border ${tone} p-3 space-y-2.5`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-1.5">
          <Link2 size={11}/> Live Connect
        </div>
        <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 dark:text-white/40">
          Email + chat + live edge
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <NodeSearchBar
            nodes={nodes}
            value={fromId}
            onSelect={(n) => setFromId(n?.id || null)}
            placeholder="From node…"
            isDark={isDark}
            size="sm"
            testId="connect-from"
          />
        </div>
        <ArrowRight size={14} className="shrink-0 text-slate-400 dark:text-white/40"/>
        <div className="flex-1 min-w-0">
          <NodeSearchBar
            nodes={nodes}
            value={toId}
            onSelect={(n) => setToId(n?.id || null)}
            placeholder="To node…"
            isDark={isDark}
            size="sm"
            testId="connect-to"
          />
        </div>
      </div>

      <textarea
        data-testid="connect-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Intro note (optional) — appears in the email + first chat message."
        rows={2}
        maxLength={2000}
        className={`w-full text-xs rounded-md border px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/30 focus:border-[#8B3FC5]
          ${isDark
            ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35'
            : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'}`}
      />

      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-slate-500 dark:text-white/45 truncate">
          {fromNode && toNode
            ? <><span className="font-bold">{fromNode.label}</span> → <span className="font-bold">{toNode.label}</span></>
            : 'Pick a from-node and a to-node, then click Connect.'}
        </div>
        <button
          data-testid="shura-connect-btn"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-[#0A0A0A] text-[#8B3FC5]
                     border border-[#8B3FC5] hover:bg-[#1a1a1a]
                     text-[10px] uppercase tracking-[0.22em] font-bold
                     disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          {busy
            ? <><Loader2 size={11} className="animate-spin"/> Connecting…</>
            : <><Link2 size={11}/> Connect</>}
        </button>
      </div>

      {lastResult && (
        <div
          data-testid="connect-last-result"
          className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5"
        >
          <CheckCircle2 size={11}/>
          Connected <b>{lastResult.from?.label}</b> ↔ <b>{lastResult.to?.label}</b>
          {lastResult.chat_thread_id && <> · chat opened</>}
          {!!lastResult.emailed?.length && <> · {lastResult.emailed.length} emailed</>}
        </div>
      )}
    </div>
  );
}
